import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { studioApi, idempotencyKey } from '../data/studioApi';
import { saveEntry } from '../data/entry';
import { attributionMode, attributionPayload } from '../data/attribution';
import { studioTrackCustom } from '../data/studioPixel';
import { normalizeHandle } from '../enums/platforms';
import useStableActions from './useStableActions';

const PLATFORM = 'instagram';
// The account from the locale alone, then every profile from the @ (contract 2.0.0).
const ONBOARDING_CONTRACT = '2.0.0';
const CONFIRMED_KEY = 'ginastee:studio:onboarding:confirmed';
// Lookup failures stay inline in the @ input, never in the global banner.
const LOOKUP_ERRORS = { social_account_not_found: 'notFound', platform_unavailable: 'platform', service_unavailable: 'platform', rate_limited: 'busy',
  too_many_requests: 'busy', network: 'network', onboarding_unavailable: 'unavailable', profile_already_connected: 'connected', profile_limit_reached: 'limit' };
// A 422 is about the @ only when it names the handle; `account.platform` means the
// platform cannot be looked up here (core: no lookup configured).
const lookupError = failure => {
  if (failure.code !== 'validation_error') return LOOKUP_ERRORS[failure.code] || 'generic';
  const fields = Object.keys(failure.errors || {});
  if (fields.some(field => /(^|\.)handle$/.test(field))) return 'invalid';
  return fields.includes('account.platform') ? 'unavailable' : 'generic';
};
const RETRY_COMPLETION = ['onboarding_incomplete', 'onboarding_not_open', 'version_conflict'];
export const CONVERSATION_STAGES = ['analyzing', 'questioning', 'memory', 'completing'];
const idle = { status: 'idle', error: null, handle: '' };

const atLeast = (version, minimum) => {
  const have = String(version || '0').split('.').map(Number);
  const want = minimum.split('.').map(Number);
  for (let i = 0; i < want.length; i++) if ((have[i] || 0) !== want[i]) return (have[i] || 0) > want[i];
  return true;
};
const readConfirmed = () => { try { return window.sessionStorage.getItem(CONFIRMED_KEY); } catch { return null; } };
// Only an account the lookup actually found (name or counts) is offered to claim.
const claimable = found => !!found && (!!found.name || found.followers_count != null || found.media_count != null);
const sameHandle = (a, b) => !!a?.handle && !!b?.handle && a.platform === b.platform && a.handle.toLowerCase() === b.handle.toLowerCase();

// Onboarding from the Instagram @ (required, no way around it). The first @ creates the
// account (its initial credits, `onboarding_status: pending`), then `/studio/onboarding`
// looks the account up, opens a profile (`profile_id`) and analyzes it; the questions come
// after the analysis, and completing connects that profile as a workspace with the answers
// (contract 2.0.0: every profile alike, none primary). A pending account resumes here after
// a reload. Anonymous visitors type the @ before signing in; it crosses the login in the
// entry, as the prompt does — for a creator who already has profiles it is another one.
//
// Another Instagram profile runs the same flow (`startAdding`, `features.multiple_profiles`);
// completing any of them makes it the active profile.
//
// stage: loading · handle · confirm · analyzing · questioning · memory · completing
//        · ready (a profile connected) · off (account failed or unavailable)
export default function useOnboarding({ signed, account, setAccount, loadAccount, locale, pollMs, entry, persistEntry, goToAuth, act, setError, setModal, mounted, profileAdded }) {
  const { creator, capabilities } = account;
  const record = account.onboarding || null;
  const pendingCreator = creator?.onboarding_status === 'pending';
  // Only a backend that can create from the @ and look Instagram up runs the flow.
  const supported = !!capabilities && atLeast(capabilities.contract_version, ONBOARDING_CONTRACT)
    && capabilities.features?.social_onboarding === true && !!capabilities.onboarding_platforms?.includes(PLATFORM);
  const [lookup, setLookup] = useState(idle);
  const [draft, setDraftValue] = useState(() => entry.instagram_handle || '');
  const [restarting, setRestarting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [confirmedId, setConfirmedId] = useState(readConfirmed);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [pollTick, setPollTick] = useState(0);
  const lock = useRef(false);
  const creatorKey = useRef(idempotencyKey('creator'));
  const completeKey = useRef(idempotencyKey('onboarding'));
  const keep = useCallback(next => setAccount(previous => ({ ...previous, onboarding: next })), [setAccount]);

  const questions = record?.questions || [];
  const pending = questions.find(q => !record.answers.some(a => a.question_id === q.id)) || null;
  const question = editing ? questions.find(q => q.id === editing) || null : pending;
  const stage = !signed ? 'handle'
    : account.status === 'loading' ? 'loading'
    : account.status !== 'ready' ? 'off'
    : creator && !pendingCreator && !adding ? 'ready'
    : completing ? 'completing'
    : !record || restarting || record.status === 'completed' ? 'handle'
    : claimable(record.account) && confirmedId !== record.id && !record.answers.length ? 'confirm'
    : record.status === 'analyzing' ? 'analyzing'
    : question ? 'questioning' : 'memory';

  const remember = id => {
    setConfirmedId(id);
    try { window.sessionStorage.setItem(CONFIRMED_KEY, id); } catch { /* Asked again after a reload. */ }
  };
  const setDraft = value => { setDraftValue(value.replace(/^@+/, '')); if (lookup.status === 'error') setLookup(idle); };
  // A pending account back on the @ input sees the @ its onboarding looked up.
  useEffect(() => {
    if (pendingCreator && !draft && record?.account?.handle) setDraftValue(record.account.handle);
  }, [creator?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const begin = async target => {
    setDraftValue(target.handle);
    setError(null);
    // A draft prompt from before sign-in is offered when the profile is ready, never sent by
    // itself. The @ stays in the entry: a failed lookup comes back to it after a reload.
    if (entry.resume || entry.onboarding || entry.instagram_handle !== target.handle) persistEntry({ ...entry, instagram_handle: target.handle, onboarding: null, resume: false });
    if (!supported) {
      console.warn(`Studio onboarding needs contract ${ONBOARDING_CONTRACT}, features.social_onboarding and an Instagram lookup (onboarding_platforms).`);
      setLookup({ status: 'error', error: 'unavailable', handle: target.handle });
      return false;
    }
    setLookup({ status: 'searching', error: null, handle: target.handle });
    try {
      // 1. The account (only once: retries reuse it). Both calls carry the ad attribution kept
      // on arrival (contract 2.6.0): core qualifies the sign-up and tells Meta from the server.
      if (!creator) {
        const created = await studioApi.createCreator({ locale, attribution: attributionPayload() }, creatorKey.current);
        creatorKey.current = idempotencyKey('creator');
        if (mounted.current) setAccount(previous => ({ ...previous, creator: created }));
        // Its initial credits show in the header right away.
        studioApi.usage().then(usage => { if (mounted.current) setAccount(previous => ({ ...previous, usage })); }).catch(() => null);
      }
      // 2. Map the profile: lookup now, analysis in the queue, questions after it. The
      // profile joins the account's profiles only once its onboarding completes.
      const next = await studioApi.startOnboarding({ account: target, locale, attribution: attributionPayload() }, idempotencyKey('onboarding'));
      if (!mounted.current) return false;
      keep(next);
      setRestarting(false); setEditing(null); setLookup(idle);
      return true;
    } catch (failure) {
      if (!mounted.current) return false;
      // Created elsewhere meanwhile: reload decides whether it still needs the onboarding.
      if (failure.code === 'creator_already_exists') { setLookup(idle); await loadAccount(); return false; }
      setLookup({ status: 'error', error: lookupError(failure), handle: target.handle });
      return false;
    }
  };
  // Before sign-in the @ waits in the entry, with the handle the profile form also reads.
  const start = (raw = draft) => {
    const handle = normalizeHandle(raw, PLATFORM);
    if (!handle) { setLookup({ status: 'error', error: 'invalid', handle: raw }); return Promise.resolve(false); }
    const target = { platform: PLATFORM, handle };
    if (signed) return begin(target);
    try { saveEntry({ ...entry, instagram_handle: handle, onboarding: { account: target }, resume: false }); goToAuth(); }
    catch { setError('storage'); }
    return Promise.resolve(false);
  };

  // Connecting another profile: the account keeps no onboarding while it has profiles, so the
  // one of a profile left open is read again.
  const openAdding = () => { setAdding(true); setRestarting(false); setEditing(null); setLookup(idle); setError(null); };
  const leftOpen = () => studioApi.onboarding().then(open => (open?.profile_id && open.status !== 'completed' ? open : null)).catch(() => null);
  // The @ a creator with profiles typed before signing in: a profile already connected opens,
  // any other is connected next to them (a refusal, as the limit, stays in the @ input).
  // Without multiple profiles the Studio keeps the one it has.
  const addFromEntry = async wanted => {
    const known = (creator.profiles || []).find(item => sameHandle(item, wanted));
    if (known || !capabilities?.features?.multiple_profiles) {
      persistEntry({ ...entry, onboarding: null, resume: false });
      if (known) profileAdded?.current?.(known.id);
      return;
    }
    openAdding(); setDraftValue(wanted.handle);
    const open = await leftOpen();
    if (!mounted.current) return;
    if (sameHandle(open?.account, wanted)) { keep(open); persistEntry({ ...entry, onboarding: null, resume: false }); }
    else begin(wanted);
  };

  // Back from sign-in: run the @ the visitor typed, unless that onboarding is already open.
  useEffect(() => {
    const wanted = entry.onboarding?.account;
    if (!entry.onboarding || !signed || account.status !== 'ready') return;
    if (!wanted) { persistEntry({ ...entry, onboarding: null }); return; }
    if (creator && !pendingCreator) { addFromEntry(wanted); return; }
    const open = record && record.status !== 'completed' && record.account?.handle === wanted.handle;
    if (open) persistEntry({ ...entry, onboarding: null, resume: false });
    else begin(wanted);
  }, [signed, account.status, creator?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // The analysis runs in the queue: poll until the questions are ready.
  useEffect(() => {
    if (!signed || record?.status !== 'analyzing') return undefined;
    const timer = setTimeout(() => {
      studioApi.onboarding().then(next => { if (mounted.current) keep(next); })
        .catch(() => { if (mounted.current) setPollTick(count => count + 1); });
    }, pollMs);
    return () => clearTimeout(timer);
  }, [signed, record, pollMs, pollTick, keep, mounted]);

  // Completed in another tab: reload the creator.
  useEffect(() => {
    if (record?.status === 'completed' && pendingCreator && !completing) loadAccount();
  }, [record?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async fn => {
    if (lock.current) return false;
    lock.current = true; setBusy(true);
    try { return await act(fn); }
    finally { lock.current = false; if (mounted.current) setBusy(false); }
  };
  // response: { kind: 'confirm' | 'skip' } · { kind: 'answer', text } · { kind: 'choose', options, custom }
  const answer = (target, response) => run(async () => {
    const send = version => studioApi.answerOnboarding({ expected_version: version, question_id: target.id, ...response }, idempotencyKey('onboarding-answer'));
    let next;
    try { next = await send(record.version); }
    catch (failure) {
      // A new answer replaces the previous one, so it is safe to resend on the newer version.
      if (failure.code !== 'version_conflict') throw failure;
      next = await send((await studioApi.onboarding()).version);
    }
    if (mounted.current) { keep(next); setEditing(null); }
    return next;
  });
  // 3. The answers connect the profile; the home moves on to format → prompt, in it.
  const complete = memoryEnabled => run(async () => {
    setCompleting(true);
    try {
      const next = await studioApi.completeOnboarding({ intent: 'complete', expected_version: record.version, memory_enabled: memoryEnabled }, completeKey.current);
      completeKey.current = idempotencyKey('onboarding');
      keep(next);
      await loadAccount();
      // Back to creating, in the profile just connected.
      if (mounted.current) { if (adding) setAdding(false); profileAdded?.current?.(next.profile_id); }
      // The creator meets the finished profile in a modal before the first prompt.
      setModal({ type: 'profileReady', account: next.account || record.account, added: adding });
      return next;
    } catch (failure) {
      if (failure.code !== 'network') completeKey.current = idempotencyKey('onboarding');
      if (RETRY_COMPLETION.includes(failure.code)) studioApi.onboarding().then(next => { if (mounted.current) keep(next); }).catch(() => null);
      throw failure;
    } finally { if (mounted.current) setCompleting(false); }
  });

  // Another profile: the @ input on the home, or the onboarding of a profile left open.
  const startAdding = async () => {
    openAdding(); setDraftValue('');
    const open = await leftOpen();
    if (mounted.current) keep(open);
  };
  const stopAdding = () => {
    if (!adding) return;
    setAdding(false); setRestarting(false); setEditing(null); setLookup(idle); keep(null);
  };

  const actions = useStableActions({
    startAdding, stopAdding, setDraft, start,
    confirmAccount: () => {
      remember(record.id);
      studioTrackCustom('StudioHandleConfirmed', attributionMode() ? { mode: attributionMode() } : {});
    },
    rejectAccount: () => { setDraftValue(record.account?.handle || ''); setRestarting(true); setLookup(idle); },
    answer, cancelEdit: () => setEditing(null), complete,
  });
  const conversation = CONVERSATION_STAGES.includes(stage);
  const questionNumber = question ? questions.indexOf(question) + 1 : 0;
  const total = questions.length;
  // One identity while nothing changed: the composer and the header are memoized on `onboarding`.
  return useMemo(() => ({ stage, conversation, record, question, supported, adding, questionNumber, total, editing, busy, lookup, draft, edit: setEditing, ...actions }),
    [stage, conversation, record, question, supported, adding, questionNumber, total, editing, busy, lookup, draft, actions]);
}
